import { useState } from 'react'
import { Modal } from './Modal'
import { principalClassLabel } from '../theme/vocabulary'
import type { PrincipalClass } from '../api/types'
import { Notice } from './primitives/Notice'

// Pre-rotation confirmation (ATC-060). Rotation is a re-key, not a "view": the
// handler mints a new token and DELETES the old hash row (Patch-CustomerEnvironment),
// so the current token is permanently invalidated. Nothing calls the API until the
// operator acknowledges the impact. See ATC-ADR-011 (hashed-at-rest, one-time reveal).
export function ConfirmRotateModal({
  customerEnvironmentId,
  customerName,
  principalClass,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  customerEnvironmentId: string
  customerName: string
  /** ATC-131: which credential. ONE line of copy; nothing else changes. */
  principalClass: PrincipalClass
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const [ack, setAck] = useState(false)

  // ATC-111: `deliberate` — this dialog is gated on the acknowledgement
  // checkbox below, so a backdrop click or a reflexive Escape must not dismiss
  // it. Cancel and the ✕ still do. Nothing else changes: the amber-trigger →
  // red-confirm escalation, the checkbox, the impact checklist and the named
  // downstream break are the FD-ADR-038 §1.11 Tier 1 standard as they stand.
  return (
    <Modal title="Rotate license token?" onClose={onCancel} dismissal="deliberate">
      {/* ATC-136: deliberately NOT migrated to `Notice`. This is STATIC impact
          copy rendered on open, and `Notice tone="error"` carries
          `role="alert"`, which most screen readers announce on mount — so it
          would interrupt the dialog's own title and heading. The dynamic error
          below IS a Notice, because an error appearing after a failed attempt
          is exactly what an alert is for. */}
      <div className="notice notice-error">
        This issues a new token and <strong>permanently invalidates the current one</strong>.
        Within ~60 seconds, everything using the old token starts returning 403 until updated.
      </div>

      <p>
        Rotating <strong>{customerName || 'this environment'}</strong>{' '}
        (<code className="cei">{customerEnvironmentId}</code>){' '}
        {/* ATC-131 adds this clause and nothing else. Naming WHICH credential
            strengthens the "name the downstream break" standard FD-ADR-038
            §1.11 promoted from this dialog — it does not restyle it. On an
            environment holding both, "rotate the token" is ambiguous, and the
            two have completely different downstream consumers. */}
        — the <strong>{principalClassLabel(principalClass)}</strong> credential — breaks the
        following until the new token is re-propagated:
      </p>
      <ul className="checklist">
        <li>
          the Flight Deck Manager’s <code>RUBIX_LICENSE_TOKEN</code> app setting
          <span className="muted"> (Flight Deck repo — a cross-repo change)</span>
        </li>
        <li>
          any devices with this token baked into their MSI install arguments
          <span className="muted"> (require re-provisioning — cannot be updated remotely)</span>
        </li>
      </ul>
      <p className="muted">
        Entitlements and account mapping are preserved. This cannot be undone, and the new
        token is shown only once.
      </p>

      {error && <Notice tone="error">{error}</Notice>}

      <label className="ack-row">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        <span>I understand the current token will stop working.</span>
      </label>

      <div className="modal-actions">
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className="btn-danger" disabled={!ack || busy} onClick={onConfirm}>
          {busy ? 'Rotating…' : 'Rotate token'}
        </button>
      </div>
    </Modal>
  )
}
